import { useEffect, useState } from "react"
import type { AvatarModel, AvatarPolarity } from "@domo/shared"
import { usePlayerStore } from "../../stores/usePlayerStore"
import { modelPalette } from "../Avatar/modelPalette"

// Sprint 5 — Menú top-right del Domo Cero.
//
// Muestra la firma actual del Tripulante (Material + Polaridad) como
// un pill compacto. Click → expande un panel pequeño con la firma en
// detalle + indicación de cómo cambiarla.
//
// Solo se renderiza cuando phase === "incarnated". Durante void /
// descending / materializing está oculto.
//
// 2026-05-06 — El botón "ELEGIR AVATAR" se removió. La reconfiguración
// ahora pasa por caminar al Portal dorado lateral del Domo: cuando el
// avatar entra al círculo, aparece el `ElectionPortalPrompt` que
// dispara `returnToVoid()` directamente. Mantenerlo como gesto
// in-world hace el rito más coherente con la mecánica del Domo (todo
// se hace caminando) y libera la esquina superior de un CTA pesado.

const MATERIAL_LABEL: Record<AvatarModel, string> = {
    cristal: "Cristal",
    plasma: "Plasma",
    biopolimero: "Biopolímero",
}

const POLARITY_LABEL: Record<AvatarPolarity, string> = {
    alfa: "Alfa",
    omega: "Omega",
}

const POLARITY_GLYPH: Record<AvatarPolarity, string> = {
    alfa: "▲", // pulso ascendente
    omega: "◯", // onda envolvente
}

export function AvatarMenu() {
    const phase = usePlayerStore((s) => s.phase)
    const model = usePlayerStore((s) => s.model)
    const polarity = usePlayerStore((s) => s.polarity)
    const [open, setOpen] = useState(false)

    // Cuando phase deja de ser "incarnated" cerramos el dropdown para
    // que al volver no aparezca abierto.
    useEffect(() => {
        if (phase !== "incarnated") {
            setOpen(false)
        }
    }, [phase])

    if (phase !== "incarnated" || !model || !polarity) return null

    const palette = modelPalette(model, polarity)

    return (
        <div
            className="fixed z-20 select-none"
            style={{
                top: "calc(env(safe-area-inset-top, 0px) + 16px)",
                right: "calc(env(safe-area-inset-right, 0px) + 16px)",
                userSelect: "none",
                WebkitUserSelect: "none",
            }}
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 14px 8px 8px",
                    borderRadius: 999,
                    border: `1px solid ${hexA(palette.avatarHalo, open ? 0.95 : 0.45)}`,
                    background: open
                        ? `linear-gradient(90deg, rgba(8,16,32,0.92), ${hexA(palette.avatarHalo, 0.18)})`
                        : "rgba(8,12,24,0.7)",
                    color: "#E0F2FF",
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: 13,
                    letterSpacing: "0.05em",
                    boxShadow: open
                        ? `0 0 24px ${hexA(palette.avatarHalo, 0.5)}`
                        : `0 4px 18px rgba(0,0,0,0.45)`,
                    cursor: "pointer",
                    backdropFilter: "blur(14px) saturate(140%)",
                    WebkitBackdropFilter: "blur(14px) saturate(140%)",
                    transition:
                        "background 220ms ease, border-color 220ms ease, box-shadow 220ms ease",
                }}
                aria-label="Menú de avatar"
            >
                <SignatureGlyph
                    model={model}
                    polarity={polarity}
                    palette={palette}
                />
                <span style={{ whiteSpace: "nowrap" }}>
                    {MATERIAL_LABEL[model]}
                    <span
                        style={{
                            opacity: 0.55,
                            margin: "0 6px",
                        }}
                    >
                        ·
                    </span>
                    {POLARITY_LABEL[polarity]}
                </span>
            </button>

            {open && (
                <div
                    style={{
                        position: "absolute",
                        top: "calc(100% + 10px)",
                        right: 0,
                        width: 280,
                        padding: 18,
                        borderRadius: 18,
                        background: "rgba(8,12,24,0.92)",
                        backdropFilter: "blur(18px) saturate(140%)",
                        WebkitBackdropFilter: "blur(18px) saturate(140%)",
                        border: `1px solid ${hexA(palette.avatarHalo, 0.32)}`,
                        boxShadow: `0 12px 36px rgba(0,0,0,0.55), 0 0 24px ${hexA(palette.avatarHalo, 0.18)}`,
                        color: "#E0F2FF",
                        fontFamily: "'Inter', system-ui, sans-serif",
                    }}
                >
                    <div
                        style={{
                            fontSize: 10,
                            letterSpacing: "0.32em",
                            color: "rgba(180,220,255,0.6)",
                            marginBottom: 6,
                        }}
                    >
                        FIRMA ACTUAL
                    </div>
                    <div
                        style={{
                            fontSize: 18,
                            fontWeight: 300,
                            marginBottom: 4,
                        }}
                    >
                        {MATERIAL_LABEL[model]} · {POLARITY_LABEL[polarity]}
                    </div>
                    <div
                        style={{
                            fontSize: 12,
                            color: "rgba(200,220,240,0.7)",
                            lineHeight: 1.5,
                            marginBottom: 12,
                        }}
                    >
                        Tu firma quedó grabada en el campo.
                    </div>

                    <div
                        style={{
                            fontSize: 11,
                            color: "rgba(255,210,122,0.92)",
                            letterSpacing: "0.18em",
                            textTransform: "uppercase",
                            padding: "10px 12px",
                            borderRadius: 12,
                            border: "1px solid rgba(212,175,55,0.45)",
                            background: "rgba(40,28,8,0.45)",
                            lineHeight: 1.55,
                        }}
                    >
                        Para mutar tu firma, camina al Portal dorado del
                        Domo. Al pisarlo, recibirás el rito de
                        Reconfiguración.
                    </div>
                </div>
            )}
        </div>
    )
}

// ── Glifo en miniatura para el pill ──────────────────────
function SignatureGlyph({
    model,
    polarity,
    palette,
}: {
    model: AvatarModel
    polarity: AvatarPolarity
    palette: ReturnType<typeof modelPalette>
}) {
    return (
        <div
            style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: `radial-gradient(circle at 35% 35%, ${hexA(palette.avatarCore, 0.95)} 0%, ${hexA(palette.avatarHalo, 0.5)} 60%, ${hexA(palette.avatarHalo, 0)} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                textShadow: `0 0 6px ${hexA(palette.avatarHalo, 0.85)}`,
                flexShrink: 0,
                border: `1px solid ${hexA(palette.avatarHalo, 0.45)}`,
            }}
            aria-hidden
        >
            {POLARITY_GLYPH[polarity]}
            <span style={{ display: "none" }}>
                {model}/{polarity}
            </span>
        </div>
    )
}

function hexA(hex: string, a: number): string {
    const v = hex.replace("#", "")
    const n = parseInt(
        v.length === 3 ? v.split("").map((c) => c + c).join("") : v,
        16
    )
    const r = (n >> 16) & 0xff
    const g = (n >> 8) & 0xff
    const b = n & 0xff
    return `rgba(${r},${g},${b},${a})`
}
